// Copyright (c) 2025 Greener Power Solutions.
// This file is part of Greener Power Solutions' software,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import { MenuItem, SvgIcon } from '@mui/material';
import { useFlag } from '@unleash/proxy-client-react';
import { Icons } from 'greener-ui-library';
import useAuth from 'hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  canCreateAsset,
  canCreateDeviceModel,
  canCreateEye,
  canCreateOrganisation,
  canCreateUser,
  canCreateUserRoles,
  canCreateWatcherType,
  canSeeFleets,
} from 'utils/authorization';

export function ManagementSelection({ onItemClick }: { onItemClick: Function }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isFleetsEnabled = useFlag('fleets');

  const menuItems = [
    {
      isVisible: true,
      to: '/account',
      testId: 'account',
      icon: Icons.User,
      title: t('navigation.profile'),
    },
    {
      isVisible: true,
      to: '/my-organisations',
      testId: 'management-my-organisations-item',
      icon: Icons.Organisation,
      title: t('navigation.myOrganisations'),
    },
    {
      isVisible: canCreateUser(user?.role),
      to: '/users',
      testId: 'management-users-item',
      icon: Icons.Users,
      title: t('navigation.users'),
    },
    {
      isVisible: canCreateUserRoles(user?.role),
      to: '/user-roles',
      testId: 'management-roles-item',
      icon: Icons.Role,
      title: t('navigation.roles'),
    },
    {
      isVisible: canCreateOrganisation(user?.role),
      to: '/organisations',
      testId: 'management-organisations-item',
      icon: Icons.Organisation,
      title: t('navigation.organizations'),
    },
    {
      isVisible: canCreateAsset(user?.role),
      to: '/greener-units',
      testId: 'management-greener-units-item',
      icon: Icons.Unit,
      title: t('navigation.units'),
    },
    {
      isVisible: canSeeFleets(user?.role) && isFleetsEnabled,
      to: '/fleets',
      testId: 'management-fleets-item',
      icon: Icons.ViewCompact,
      title: t('navigation.fleets'),
    },
    {
      isVisible: canCreateEye(user?.role),
      to: '/greener-eyes',
      testId: 'management-greener-eyes-item',
      icon: Icons.GreenerEye,
      title: t('navigation.greenerEyes'),
    },
    {
      isVisible: canCreateWatcherType(user?.role),
      to: '/watcher-types',
      testId: 'management-watcher-types-item',
      icon: Icons.WatcherType,
      title: t('navigation.watcherTypes'),
    },
    {
      isVisible: canCreateDeviceModel(user?.role),
      to: '/device-models',
      testId: 'management-device-models-item',
      icon: Icons.Devices,
      title: t('navigation.deviceModel'),
    },
  ];

  return menuItems
    .filter((item) => item.isVisible)
    .map((item) => (
      <MenuItem key={item.to} component={Link} data-testid={item.testId} to={item.to} onClick={() => onItemClick()}>
        <SvgIcon fontSize="small" color="inherit" style={{ marginRight: '0.5em' }}>
          <item.icon />
        </SvgIcon>
        {` ${item.title}`}
      </MenuItem>
    ));
}
